import { useMemo } from 'react'
import type { JSX } from 'react'
import type { ReactNode } from 'react'

import { createEngagementContext } from '../../context/EngagementContext'

import type { MentionData } from '../fragments/mentionData'

/**
 * The mention shape this context provides. Card-tree consumers derive from
 * this alias instead of the codegen entity; the shape itself lives with the
 * fragment in {@link ../fragments/mentionData} so producers can spread it.
 */
export type { MentionData } from '../fragments/mentionData'

/**
 * `MentionData['assets']` is still typed as the bare `Asset` interface, so
 * fixtures that exercise the image-narrowing paths layer the `ImageAsset`
 * branch (`__typename` + `image`) on top. Delete this (in favour of
 * `Extract<MentionData['assets'][number], { __typename: 'ImageAsset' }>`)
 * once `assets` becomes a discriminated union.
 */
export type MentionImageAssetData = MentionData['assets'][number] & {
  __typename: 'ImageAsset'
  image: {
    __typename: 'ImageMetadata'
    altText: string
    width: number
    height: number
    isAnimated: boolean
  }
}

/**
 * Stable empty default so cards without replies don't bust the context memo
 * with a fresh array each render.
 */
const NO_REPLIES: MentionData[] = []

/**
 * The mention's feature-specific `extra` payload. Mentions carry their own
 * reply list; Comments have no equivalent (their replies arrive via
 * `lastReplies` on the comment + sent replies elsewhere).
 */
type MentionExtra = {
  /**
   * The mention's own replies, oldest-first. Populated only for the root
   * inbox card (the list fetches them); empty for context/reply rows.
   */
  replies: MentionData[]
}

// ========================================
// Context Definition
// ========================================

/**
 * MentionContext is a thin Mentions-specific wrapper over the shared
 * `EngagementContext`, adding the mention-only `replies` payload via `extra`.
 */
const { EngagementContext, EngagementContextProvider, useEngagementContext } =
  createEngagementContext<MentionData, MentionExtra>({
    providerName: 'MentionContextProvider',
    hookName: 'useMentionContext',
  })

export const MentionContext = EngagementContext

// ========================================
// Provider Component
// ========================================

export type MentionProviderProps = {
  mention: MentionData
  /** The mention's replies, oldest-first. Only the root inbox card sets this. */
  replies?: MentionData[]
  children: ReactNode
}

export const MentionContextProvider = ({
  mention,
  replies = NO_REPLIES,
  children,
}: MentionProviderProps): JSX.Element => {
  const extra = useMemo<MentionExtra>(() => ({ replies }), [replies])

  return (
    <EngagementContextProvider engagement={mention} extra={extra}>
      {children}
    </EngagementContextProvider>
  )
}

MentionContextProvider.displayName = 'MentionContextProvider'

// ========================================
// Context Hook
// ========================================

export const useMentionContext = (): {
  mention: MentionData
  replies: MentionData[]
  reactionError: string | null
  setReactionError: (error: string | null) => void
} => {
  const { engagement, extra, reactionError, setReactionError } =
    useEngagementContext()
  return {
    mention: engagement,
    replies: extra.replies,
    reactionError,
    setReactionError,
  }
}

// ========================================
// Utility Hook
// ========================================

export const useMentionData = (): MentionData => {
  const { engagement } = useEngagementContext()
  return engagement
}

/**
 * The current mention's replies (oldest-first), as provided by the list.
 * Empty unless this is a root inbox card with replies.
 */
export const useMentionReplies = (): MentionData[] => {
  const { extra } = useEngagementContext()
  return extra.replies
}

export const useMentionReactionError = (): {
  reactionError: string | null
  setReactionError: (error: string | null) => void
} => {
  const { reactionError, setReactionError } = useEngagementContext()
  return { reactionError, setReactionError }
}
